import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasSelection } from '@peruselab/structure-editor/domain/editor';
import { alignableObjectsInSelection } from '@peruselab/structure-editor/domain/objectAlignment';
import { Icon } from '../Icon';
import { ALIGN, DISTRIBUTE, ORDER } from './arrangeActions';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';

// #855 (#841 A1-A9) -- the Arrange tab: Order, Align and Distribute, in
// that order. Every control stays in place whatever is selected (A8); a disabled one says why in
// its tooltip (A9) -- aria-disabled rather than `disabled`, since browsers drop the tooltip of a
// disabled button. Group arrives with #768 and Lock with #770 (A7).
export function ArrangePanel({ doc }: { readonly doc: UseDrawingDocumentResult }) {
  const { t } = useTranslation();
  const [relativeTo, setRelativeTo] = useState<'objects' | 'page'>('objects');

  const { selection } = doc;
  const objects = alignableObjectsInSelection(doc.document, selection, doc.reactionParts);
  const nothingSelected = !hasSelection(selection);
  const annotationCount = selection.annotationIds?.length ?? 0;
  const hasMolecule = selection.atomIds.length > 0 || selection.bondIds.length > 0;

  const orderReason = nothingSelected
    ? t('rightRail.arrange.selectObjects')
    : hasMolecule
      ? t('rightRail.arrange.moleculesBeneath')
      : annotationCount === 0
        ? t('rightRail.arrange.selectObjects')
        : null;
  const alignMinimum = relativeTo === 'page' ? 1 : 2;
  const alignReason =
    objects.length >= alignMinimum
      ? null
      : relativeTo === 'page'
        ? t('rightRail.arrange.selectObjects')
        : t('rightRail.arrange.selectTwo');
  const distributeReason = objects.length >= 3 ? null : t('rightRail.arrange.selectThree');

  const label = (base: string, reason: string | null) => (reason ? `${base} — ${reason}` : base);
  return (
    <div className="arrange-panel" data-testid="arrange-panel">
      {nothingSelected ? <p className="rail-hint arrange-hint">{t('rightRail.arrange.selectObjects')}</p> : null}

      <section className="rail-section" aria-labelledby="arrange-order-heading">
        <h2 id="arrange-order-heading">{t('rightRail.arrange.order')}</h2>
        <div className="rail-action-grid arrange-icons">
          {ORDER.map(([command, icon]) => {
            const name = label(t(`rightRail.arrange.${command}`), orderReason);
            return (
              <button
                key={command}
                type="button"
                className="icon-button"
                title={name}
                aria-label={t(`rightRail.arrange.${command}`)}
                aria-disabled={orderReason !== null || undefined}
                onClick={() => {
                  if (orderReason === null) doc.reorderSelection(command);
                }}
              >
                <Icon name={icon} />
              </button>
            );
          })}
        </div>
      </section>

      <section className="rail-section" aria-labelledby="arrange-align-heading">
        <h2 id="arrange-align-heading">{t('rightRail.align')}</h2>
        <div className="rail-action-grid arrange-align-actions">
          {ALIGN.map(([kind, icon, key]) => (
            <button
              key={kind}
              type="button"
              className="icon-button"
              title={label(t(`editorToolPalette.align.${key}`), alignReason)}
              aria-label={t(`editorToolPalette.align.${key}`)}
              aria-disabled={alignReason !== null || undefined}
              onClick={() => {
                if (alignReason === null) doc.alignSelection(kind, { relativeTo });
              }}
            >
              <Icon name={icon} />
            </button>
          ))}
        </div>
        <div className="arrange-relative" role="radiogroup" aria-label={t('rightRail.arrange.relativeTo')}>
          <span>{t('rightRail.arrange.relativeTo')}</span>
          {(['objects', 'page'] as const).map((value) => (
            <label key={value} className={relativeTo === value ? 'active' : undefined}>
              <input
                type="radio"
                name="arrange-relative-to"
                checked={relativeTo === value}
                onChange={() => setRelativeTo(value)}
              />
              {t(`rightRail.arrange.${value}`)}
            </label>
          ))}
        </div>
      </section>

      <section className="rail-section arrange-distribute" aria-labelledby="arrange-distribute-heading">
        <h2 id="arrange-distribute-heading">{t('rightRail.arrange.distribute')}</h2>
        <div className="rail-action-grid">
          {DISTRIBUTE.map(([kind, icon, key]) => (
            <button
              key={kind}
              type="button"
              className="icon-button"
              title={label(t(`editorToolPalette.align.${key}`), distributeReason)}
              aria-label={t(`editorToolPalette.align.${key}`)}
              aria-disabled={distributeReason !== null || undefined}
              onClick={() => {
                if (distributeReason === null) doc.alignSelection(kind);
              }}
            >
              <Icon name={icon} />
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
